// store.js
// Zustand global state management
// Single source of truth for all app state

import { create } from 'zustand';

// ============================================================
// DEFAULT CHART SETTINGS — one object per chart slot
// ============================================================
const defaultChartSetting = () => ({
  timeframe:   '1D',
  chartType:   'candlestick',       // 'candlestick' | 'line'
  lineSources: ['close'],           // which price lines shown on line chart
  emaSource:   'close',             // which field EMAs are calculated from
  visibleEMAs: {
    ema_5:   false,
    ema_9:   false,
    ema_20:  false,
    ema_50:  false,
    ema_200: false,
  },
  // Style per price line source
  lineStyles: {
    close: { color: '#131722', width: 2 },
    high:  { color: '#26a69a', width: 2 },
    low:   { color: '#ef5350', width: 2 },
  },
  // Style per EMA period
  emaStyles: {
    ema_5:   { color: '#2962ff', width: 1 },
    ema_9:   { color: '#ff6d00', width: 1 },
    ema_20:  { color: '#2e7d32', width: 1 },
    ema_50:  { color: '#6a1b9a', width: 1 },
    ema_200: { color: '#b71c1c', width: 1 },
  },
  showTrendLine:        false,
  showTrendLineMarkers: false,   // H/L pivot letters — off by default
  showTrendlineEngine:  false,   // ── TREND LINE ENGINE (new) ──
  trendlineN:           3,       // ── TREND LINE ENGINE (new) — pivot lookback ──
});

// Module-level SSE instance — survives component unmount/remount
let _sseInstance = null;
const useStore = create((set) => ({

  // ============================================================
  // SELECTED STOCK
  // ============================================================
  selectedSymbol: '',
  setSelectedSymbol: (symbol) => set({ selectedSymbol: symbol }),

  // ============================================================
  // CHART SETTINGS — per chart slot (0–3)
  // Single chart uses slot 0 only.
  // Grid uses all 4 slots independently.
  // ============================================================
  chartSettings: [
    defaultChartSetting(),
    defaultChartSetting(),
    defaultChartSetting(),
    defaultChartSetting(),
  ],

  // Update any key in a single chart slot
  setChartSetting: (index, key, value) =>
    set((state) => {
      const updated = [...state.chartSettings];
      updated[index] = { ...updated[index], [key]: value };
      return { chartSettings: updated };
    }),

  // Toggle one EMA period for a chart slot
  toggleChartEMA: (index, emaKey) =>
    set((state) => {
      const updated       = [...state.chartSettings];
      const slot          = { ...updated[index] };
      slot.visibleEMAs    = {
        ...slot.visibleEMAs,
        [emaKey]: !slot.visibleEMAs[emaKey],
      };
      updated[index] = slot;
      return { chartSettings: updated };
    }),
  toggleTrendLine: (index) =>
    set((state) => {
      const updated = [...state.chartSettings];
      updated[index] = {
        ...updated[index],
        showTrendLine: !updated[index].showTrendLine,
      };
      return { chartSettings: updated };
  }),
  toggleTrendlineEngine: (index) =>
  set((state) => {
    const updated = [...state.chartSettings];
    updated[index] = {
      ...updated[index],
      showTrendlineEngine: !updated[index].showTrendlineEngine,
    };
    return { chartSettings: updated };
  }),
  toggleTrendLineMarkers: (index) =>
    set((state) => {
      const updated = [...state.chartSettings];
      updated[index] = {
        ...updated[index],
        showTrendLineMarkers: !updated[index].showTrendLineMarkers,
      };
      return { chartSettings: updated };
  }),

  // ============================================================
  // CANDLE DATA — keyed by "symbol_timeframe"
  // ============================================================
  candleData: {},
  setCandleData: (key, data) =>
    set((state) => ({
      candleData: { ...state.candleData, [key]: data },
    })),

  // ============================================================
  // INDICATOR DATA — keyed by "symbol_timeframe"
  // ============================================================
  indicatorData: {},
  setIndicatorData: (key, data) =>
    set((state) => ({
      indicatorData: { ...state.indicatorData, [key]: data },
    })),

  // ============================================================
  // TRADES — uploaded from CSV
  // ============================================================
  trades: [],
  setTrades: (trades) => set({ trades }),

  // ============================================================
  // WATCHLIST
  // ============================================================
  watchlist: [],
  setWatchlist: (watchlist) => set({ watchlist }),

  // ============================================================
  // PLAY MODE
  // Auto-cycle through watchlist symbols on a timer
  // ============================================================
  playMode:        false,
  playInterval:    5,
  setPlayMode:     (val) => set({ playMode: val }),
  setPlayInterval: (val) => set({ playInterval: val }),

  // ============================================================
  // MARKERS TOGGLE
  // Show/hide trade markers on charts
  // ============================================================
  showMarkers: true,
  setShowMarkers: (val) => set({ showMarkers: val }),

  // Trade review mode — true ONLY while the Trades view is open.
  // Gates trade markers so they appear only on charts opened from Trades.
  tradeReviewMode: false,
  setTradeReviewMode: (val) => set({ tradeReviewMode: val }),

  // ============================================================
  // JUMP TRIGGER
  // When set, all active Chart components jump to this date
  // { date: 'YYYY-MM-DD', id: timestamp } — id ensures re-trigger
  // on same date works (e.g. reviewing same trade twice)
  // ============================================================
  jumpTrigger:    null,
  setJumpTrigger: (date) => set({ jumpTrigger: { date, id: Date.now() } }),

  // ============================================================
  // V PATTERNS DISPLAY
  // When true, charts show V pattern markers (crossover report mode)
  // vpFilters: ema, fromDate, toDate passed from report to chart
  // ============================================================
  showVPatterns:    false,
  setShowVPatterns: (val) => set({ showVPatterns: val }),

  vpFilters: { ema: null, fromDate: null, toDate: null, timeframe: null },
  setVpFilters: (filters) => set({ vpFilters: filters }),

  // ============================================================
  // MARKERS CONFIG — generic report markers for ChartNew
  // fetchFn: string key mapping to MARKER_FETCH_FNS in ChartNew
  // filters: any report-specific filter object
  // ============================================================
  markersConfig: { enabled: false, fetchFn: null, filters: {} },
  setMarkersConfig: (config) => set({ markersConfig: config }),

  // ============================================================
  // URL PARAMS — set on app load for new-tab chart mode
  // ============================================================
  urlWatchlistId: null,
  urlJumpDate:    null,
  setUrlParams: (watchlistId, jumpDate) =>
    set({ urlWatchlistId: watchlistId, urlJumpDate: jumpDate }),

  // ============================================================
  // LAYOUT
  // ============================================================
  layout:     'single',           // 'single' | 'grid'
  gridCount:  4,                  // 2 | 3 | 4 charts in grid
  setLayout:  (layout) => set({ layout }),
  setGridCount: (count) => set({ gridCount: count }),

  // ============================================================
  // CROSSHAIR SYNC — shared across grid charts
  // syncedTime: unix timestamp (seconds) of current crosshair
  // syncSource: gridIndex of chart that triggered the sync
  //             other charts check this to avoid echo loops
  // ============================================================
  syncedTime:   null,
  syncSource:   null,
  setSyncedTime: (time, sourceIndex) => set({
    syncedTime: time,
    syncSource: sourceIndex,
  }),

  // ============================================================
  // ACTIVE VIEW
  // ============================================================
  activeView:    'charts',
  setActiveView: (view) => set({ activeView: view }),

  // ============================================================
  // AUTH
  // ============================================================
  token: localStorage.getItem('trading_token') || null,
  setToken: (val) => {
    if (val) localStorage.setItem('trading_token', val);
    else localStorage.removeItem('trading_token');
    set({ token: val });
  },
  clearToken: () => {
    localStorage.removeItem('trading_token');
    set({ token: null });
  },
  // ============================================================
  // LIVE ALERTS — SSE state persists across navigation
  // _sseInstance is module-level (not in Zustand) because
  // EventSource is not serializable
  // ============================================================
  alerts:     [],
  isRunning: localStorage.getItem('sse_running') === 'true',
  addAlert:   (alert) =>
    set((state) => ({ alerts: [...state.alerts, alert] })),
  setAlerts:  (alerts) => set({ alerts }),
  setIsRunning: (val) => set({ isRunning: val }),

}));

export default useStore;

// ============================================================
// SSE LIFECYCLE — called from AlertsView (not inside Zustand
// because closures over set() are cleaner this way)
// ============================================================
export function startSSE(apiBase) {
  if (_sseInstance) return;                         // already running

  const es = new EventSource(`${apiBase}/alerts/stream`);

  es.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'connected') return;
      useStore.getState().addAlert(data);           // push into Zustand
    } catch (_) {}
  };

  es.onerror = () => {
    // Browser auto-reconnects EventSource — no action needed
  };

_sseInstance = es;
localStorage.setItem('sse_running', 'true');
useStore.getState().setIsRunning(true);
}

export function stopSSE() {
  if (_sseInstance) {
    _sseInstance.close();
    _sseInstance = null;
  }
  localStorage.removeItem('sse_running');
  useStore.getState().setIsRunning(false);
}

